import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getOrders, getBranches } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

function money(n) {
  return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Orders() {
  const { t } = useTranslation("orders");

  const STATUS_OPTIONS = [
    { value: "", label: t("status_option_all", "All statuses") },
    { value: "completed", label: t("status_option_completed", "Completed") },
    { value: "cancelled", label: t("status_option_cancelled", "Cancelled") },
  ];

  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchOrders = async () => {
    try {
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getOrders(token, params);
      setOrders(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchOrders(); }, [branchFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { getBranches(token).then(setBranches).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branchOptions = [{ value: "", label: t("filter_branch_all_option", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];

  const sortedAndFiltered = useMemo(() => {
    let result = [...orders];
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [orders, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const columns = [
    { key: "id", label: t("col_order_number", "Order #"), className: "text-gray-700 font-mono font-bold", render: (row) => `#${row.id}` },
    { key: "invoice_number", label: t("col_invoice_number", "Invoice #"), className: "text-gray-500 font-mono", render: (row) => row.invoice_number || "—" },
    { key: "created_at", label: t("col_date", "Date"), className: "text-gray-500", render: (row) => new Date(row.created_at).toLocaleString() },
    { key: "branch", label: t("col_branch", "Branch"), className: "text-gray-600", render: (row) => row.branch?.name || `#${row.branch_id}` },
    { key: "cashier", label: t("col_cashier", "Cashier"), className: "text-gray-600", render: (row) => row.cashier?.username || `#${row.cashier_id}` },
    { key: "customer", label: t("col_customer", "Customer"), className: "text-gray-600", render: (row) => row.customer?.name || t("customer_walkin", "Walk-in") },
    {
      key: "order_type",
      label: t("col_type", "Type"),
      className: "text-gray-600 capitalize",
      render: (row) => (
        <span className="flex items-center gap-1.5">
          {row.order_type?.replace("_", " ")}
          {/* Only set on a dine-in order that actually picked a table — see
              Order.TableID in models/order.go. */}
          {row.table && <span className="text-[10px] text-gray-400 font-mono normal-case">· {row.table.name}</span>}
        </span>
      ),
    },
    { key: "items", label: t("col_items", "Items"), className: "text-gray-500", render: (row) => row.items?.length ?? "—" },
    {
      key: "grand_total",
      label: t("col_total", "Total"),
      className: "text-gray-800 font-mono font-bold",
      render: (row) => `${money(row.grand_total)} ${row.currency?.symbol || row.currency?.code || ""}`,
    },
    {
      key: "status",
      label: t("col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.status === "completed" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
          {row.status}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Orders size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Orders")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Sales history — click a row for the full receipt")}</p>
          </div>
        </div>

        <Button variant="primary" icon={ICONS.Checkout} onClick={() => navigate("/checkout")} className="w-full sm:w-auto shrink-0">
          {t("btn_go_to_checkout", "Go to Checkout")}
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
        <SelectBox label={t("filter_branch_label", "Filter by branch")} options={branchOptions} value={branchFilter} onChange={(val) => { setBranchFilter(val); setCurrentPage(1); }} />
        <SelectBox label={t("filter_status_label", "Filter by status")} options={STATUS_OPTIONS} value={statusFilter} onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }} />
      </div>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <Button variant="outline" size="sm" icon={ICONS.Orders} onClick={() => navigate(`/orders/${row.id}`)}>{isMobile ? ` ${t("btn_view", "View")}` : ""}</Button>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">#{row.id}</span>
            <h3 className="font-bold text-gray-800">{money(row.grand_total)}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
